import { useEffect, useState, useRef } from "react"
import { useDispatch } from "react-redux"
import { setActiveTab } from "../../redux/reducer/appReducer"
import {
  ApiMethods,
  INVOICE,
  statusCodes,
  tableInitialDataCount,
} from "../../utils/constants"
import { ApiRequest } from "../../services/api-request"
import { ApiPaths } from "../../utils/apiConstants"

export const useInvociesContainer = () => {
  const [showInvoicesList, setShowInvoicesList] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState("hide")
  const [invocieList, setInvoiceList] = useState([])
  const [modalData, setModalData] = useState({ open: false, url: "" })
  const [paginationData, setPaginationData] = useState({
    count: 0,
    endReached: false,
    searchValue: "",
  })
  const observerRef = useRef(null)
  const tableContainerRef = useRef(null)
  const tableLimit = tableInitialDataCount

  const dispatch = useDispatch()

  useEffect(() => {
    setIsLoading(true)
    dispatch(setActiveTab(INVOICE))
    getInvoices(0, "")
  }, [showInvoicesList])

  const getInvoices = (offset, searchKey) => {
    setLoadingStatus(prev => {
      if(prev==="loaded") {
        return "loading"
      }
    })

    ApiRequest({
      url: ApiPaths.GET_INVOICE_LIST,
      method: ApiMethods.GET,
      params: { searchKey, offset, limit: tableLimit },
    })
      .then((response) => {
        const { data = {} } = response
        if (data?.status === statusCodes.success) {
          const { result = {} } = data
          const { invoiceDetails = [] } = result
          if (invoiceDetails) {
            if (offset === 0) {
              setInvoiceList(invoiceDetails)
            } else {
              setInvoiceList((prev) => [...prev, ...invoiceDetails])
            }
            if (invoiceDetails.length < tableLimit) {
              setPaginationData((prev) => ({ ...prev, endReached: true }))
            }
          }
        }
        setIsLoading(false)
        setLoadingStatus("loaded")
      })
      .catch((error) => {
        setIsLoading(false)
        setLoadingStatus("loaded")
      })
  }

  const handleSearchChange = (event) => {
    const text = event.target.value
    setPaginationData({ searchValue: text, count: 1, endReached: false })
    getInvoices(0, text)
  }

  const callObserver = (entries) => {
    // console.log("callObserver", entries[0].isIntersecting)
    if (entries[0].isIntersecting) {
      setPaginationData((prev) => {
        if (!prev.endReached) {
          getInvoices(prev.count, prev.searchValue)
        }
        return { ...prev, count: prev.count + 1 }
      })
    }
  }

  useEffect(() => {
    if (paginationData?.endReached) {
      setLoadingStatus("hide")
    }
  }, [paginationData?.endReached])

  useEffect(() => {
    const observer = new IntersectionObserver(callObserver, { threshold: 1 })
    if (observerRef.current) {
      observer.observe(observerRef.current)
    }
    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current)
      }
    }
  }, [observerRef, paginationData?.searchValue, invocieList, showInvoicesList])

  // Scroll to top when search value changes
  useEffect(() => {
    if (tableContainerRef.current) {
      // Try to find the TableContainer element within the ref
      const tableContainer = tableContainerRef.current.querySelector('.MuiTableContainer-root')
      if (tableContainer) {
        tableContainer.scrollTo({ top: 0, behavior: 'auto' })
      } else {
        // Fallback to the container div itself
        tableContainerRef.current.scrollTo({ top: 0, behavior: 'auto' })
      }
    }
  }, [paginationData?.searchValue])

  return {
    handleSearchChange,
    setShowInvoicesList,
    showInvoicesList,
    isLoading,
    invocieList,
    setInvoiceList,
    modalData,
    setModalData,
    paginationData,
    setPaginationData,
    observerRef,
    tableContainerRef,
    loadingStatus,
  }
}
